/**
 * HexTab — the "Hex" tab of the consolidated Palette Modal V2 (decision D15).
 *
 * This is the deck-faithful Brand-Palette hex builder (BrandPaletteWizard) folded
 * IN as the modal's third tab instead of a separate standalone Modal. It reuses
 * BrandPaletteWizard's pure logic (addStop / removeStop / setStopHex / etc.) and
 * its form styles, and — crucially — it does NOT save on its own. It writes the
 * edited palette into the SAME colorPalette redux "color" bucket the Prompt/Image
 * tabs use, so the ONE modal footer "Select" button commits a hex palette exactly
 * like the auto tabs (single-source confirm, finding D-793). Retires the
 * ManualPalette draft + the commented-out dead SegmentColor tab.
 *
 * Gated: only mounted by ColorPaletteModal when PALETTE_MODAL_V2_ENABLED.
 *
 * Props:
 *   paletteType      — PALETTE_TYPES.WHEEL | PALETTE_TYPES.SEGMENT
 *   handleSelect()   — modal confirm handler (wheel-wizard in-tab Select)
 *   showSelectButton — render an in-tab Select (wheel-wizard has no footer)
 */
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addColorPalettes } from "../../../../redux/reducers/colorPalette";
import { getPaletteColors } from "../../../../redux/selectors/colorPalette";
import Button from "../../../Button";
import CustomColorPicker from "../../../CustomColorPicker";
import {
  addStop,
  makeId,
  MAX_STOPS,
  MIN_STOPS,
  removeStop,
  setStopDraft,
  setStopHex,
  snapDraft,
} from "./BrandPaletteWizard/logic";
import { stopsFromPalette, toPalette } from "./hexTabModel";
import * as S from "./BrandPaletteWizard/styles";

export default function HexTab({
  paletteType,
  handleSelect = () => {},
  showSelectButton = false,
}) {
  const dispatch = useDispatch();

  // Rehydrate from any palette already sitting in the "color" bucket (e.g. the
  // user switched away and back), else start from the deck default palette.
  const existing = useSelector((state) =>
    getPaletteColors(state, "color", paletteType)
  );
  const existingFirst = existing && existing[0];

  // One stable palette id for this editing session — NEVER Math.random/Date.now
  // in a key or id (staging gate G-57).
  const [paletteId] = useState(() => makeId());
  const [name, setName] = useState(
    () => (existingFirst && existingFirst.paletteName) || "Brand colors"
  );
  const [stops, setStops] = useState(() => stopsFromPalette(existingFirst));
  const [activeStopId, setActiveStopId] = useState(null);

  const activeStop = useMemo(
    () => stops.find((s) => s.id === activeStopId) || null,
    [stops, activeStopId]
  );

  // Mirror every edit into the shared "color" bucket so the modal's single
  // Select button (footer) commits this hex palette like the auto tabs do.
  useEffect(() => {
    dispatch(
      addColorPalettes({
        paletteType,
        type: "color",
        paletteColors: [toPalette(paletteId, name, stops)],
      })
    );
  }, [dispatch, paletteType, paletteId, name, stops]);

  return (
    <div>
      <S.FormCol>
        <S.Field>
          <S.Label htmlFor="hextab-name">Palette name</S.Label>
          <S.NameInput
            id="hextab-name"
            value={name}
            maxLength={60}
            onChange={(e) => setName(e.target.value)}
            placeholder="Brand colors"
            data-palette-hex-name
          />
        </S.Field>

        <S.Field>
          <S.ColorsHead>
            <S.Label as="div">Colors</S.Label>
            <S.CountPill data-palette-hex-count>
              {stops.length} color{stops.length === 1 ? "" : "s"}
            </S.CountPill>
          </S.ColorsHead>

          <S.StopList>
            {stops.map((stop) => {
              const active = stop.id === activeStopId;
              return (
                <S.StopRow key={stop.id} data-palette-hex-stop>
                  <S.Swatch
                    type="button"
                    color={stop.hex}
                    active={active}
                    title="Edit color"
                    aria-label={`Edit color ${stop.hex}`}
                    onClick={() => setActiveStopId(active ? null : stop.id)}
                  />
                  <S.HexField active={active}>
                    <S.HexPrefix>#</S.HexPrefix>
                    <S.HexInput
                      value={stop.draft.replace(/^#/, "")}
                      aria-label={`Hex value for color ${stop.hex}`}
                      onChange={(e) =>
                        setStops((prev) =>
                          setStopDraft(prev, stop.id, e.target.value)
                        )
                      }
                      onBlur={() => setStops((prev) => snapDraft(prev, stop.id))}
                    />
                  </S.HexField>
                  <S.IconBtn
                    type="button"
                    aria-label="Remove color"
                    title={stops.length <= MIN_STOPS ? "Last color" : "Remove"}
                    disabled={stops.length <= MIN_STOPS}
                    onClick={() =>
                      setStops((prev) => removeStop(prev, stop.id))
                    }
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </S.IconBtn>
                </S.StopRow>
              );
            })}
          </S.StopList>

          <S.AddRow
            type="button"
            title={stops.length >= MAX_STOPS ? `Max ${MAX_STOPS} colors` : "Add color"}
            disabled={stops.length >= MAX_STOPS}
            onClick={() => setStops((prev) => addStop(prev))}
            data-palette-hex-add
          >
            <S.AddLeft>
              <AddIcon fontSize="small" />
              Add color
            </S.AddLeft>
          </S.AddRow>
        </S.Field>

        {showSelectButton && (
          <Button onClick={handleSelect} title="Select" style={{ marginTop: 8 }}>
            Select
          </Button>
        )}
      </S.FormCol>

      {activeStop && (
        <CustomColorPicker
          visible={!!activeStop}
          color={activeStop.hex}
          onColorChange={(hex) =>
            setStops((prev) => setStopHex(prev, activeStop.id, hex))
          }
          onClose={() => setActiveStopId(null)}
          onConfirm={() => setActiveStopId(null)}
          customStyle={{
            position: "fixed",
            top: "50%",
            left: "50%",
            bottom: "auto",
            transform: "translate(-50%, -50%)",
            zIndex: 1400,
          }}
        />
      )}
    </div>
  );
}
